import React from 'react';
import loadable from '@loadable/component';
import { Navigate, Route, createRoutesFromChildren } from 'react-router-dom';
import RouteLoader from './components/RouteLoader';
import Loader from './components/loader';
import { ErrorProps } from './error/Error';
import FormEditorWrapper from './helpers/FormEditorWrapper';
import { canIUse } from './helpers/PermissionUtility';
import { AppFeatures, Features } from './helpers/PermissionUtility-types';
import FeatureFlagged from './routes/FeatureFlagged';
import FeatureFlaggedLayout from './routes/FeatureFlaggedLayout';
import HeaderlessAuthLayout from './routes/layouts/HeaderlessAuthLayout';
import LoggingAndRedirectLayout from './routes/layouts/LoggingAndRedirectLayout';
import PublicShareLayout from './routes/layouts/PublicShareLayout';
import MainLayout from './routes/layouts/MainLayout';
import WrappedLayout from './routes/layouts/WrappedLayout';
import EditorRedirect from './routes/redirects/EditorRedirect';
import HomeRedirect from './routes/redirects/HomeRedirect';
import IndexRedirect from './routes/redirects/IndexRedirect';
import TemplateRedirect from './routes/redirects/TemplateRedirect';
import WorkflowRedirect from './routes/redirects/WorkflowRedirect';
import ExpiredTrial from './welcome/expiredtrial/ExpiredTrial';
import UsersRedirect from './routes/redirects/UsersRedirect';
import ReportUpsell from './reports/components/ReportUpsell';
import { WorkflowRecipeProvider } from './workflows/components/WorkflowRecipeProvider';
import OnboardingSurveyRouter from './survey/OnboardingSurveyRouter';
import OnboardingFinal from './survey/OnboardingFinal';

// todo - replace with react lazy/suspense since we're not taking advantage of @loadable/component
// todo - see if creating a custom, dynamic <AsyncComponent path="<imported/component/path>" /> will work with the routes below

// remember to keep this list alphabetized!
const AccountDeleted = loadable(() => import('./components/AccountDeleted'), { fallback: <RouteLoader /> });
const AccountSettings = loadable(() => import('./settings/accountsettings/AccountSettings'), { fallback: <RouteLoader /> });
const AddGroup = loadable(() => import('./usersgroups/groups/components/group-permissions/AddGroup'), { fallback: <RouteLoader /> });
const AddUser = loadable(() => import('./usersgroups/users/components/AddUser'), { fallback: <RouteLoader /> });
const ApiCredentials = loadable(() => import('./settings/apicredentials/ApiCredentials'), { fallback: <RouteLoader /> });
const AppDetails = loadable(() => import('./apps/components/AppDetails'), { fallback: <RouteLoader /> });
const AppList = loadable(() => import('./apps/components/AppList'), { fallback: <RouteLoader /> });
const AutoEmail = loadable(() => import('./settings/autoemail/AutoEmail'), { fallback: <RouteLoader /> });
const BillingPage = loadable(() => import('./settings/billing/components/BillingPage'), { fallback: <RouteLoader /> });
const BillingProvider = loadable(() => import('./settings/billing/BillingProvider'), { fallback: <RouteLoader /> });
const BulkAddUser = loadable(() => import('./usersgroups/users/components/BulkAddUser'), { fallback: <RouteLoader /> });
const CreateTemplateModalPage = loadable(() => import('./templates/components/CreateTemplateModalPage'), { fallback: <RouteLoader /> });
const DatasourceEditor = loadable(() => import('./datasourceeditor/EditDatasource'), { fallback: <RouteLoader /> });
const DatasourceList = loadable(() => import('./datasources/components/DatasourceList'), { fallback: <RouteLoader /> });
const DesignerPage = loadable(() => import('./reports/components/DesignerPage'), { fallback: <RouteLoader /> });
const EditGroup = loadable(() => import('./usersgroups/groups/components/group-permissions/EditGroup'), { fallback: <RouteLoader /> });
const EditUser = loadable(() => import('./usersgroups/users/components/EditUser'), { fallback: <RouteLoader /> });
const Error = loadable<ErrorProps>(() => import('./error/Error'), { fallback: <RouteLoader /> });
const FormEditor = loadable(() => import('./formeditor/components/FormEditor'), { fallback: <RouteLoader /> });
const Forms = loadable(() => import('./forms/components/Forms'), { fallback: <RouteLoader /> });
const ItemCollectionEdit = loadable(() => import('./itemcollections/components/ItemCollectionEdit'), { fallback: <RouteLoader /> });
const ItemCollectionList = loadable(() => import('./itemcollections/components/ItemCollectionList'), { fallback: <RouteLoader /> });
const IuProfile = loadable(() => import('./iu-profile/IuProfile'), { fallback: <RouteLoader /> });
const JobDetails = loadable(() => import('./jobdetails/components/JobDetails'), { fallback: <RouteLoader /> });
const JobList = loadable(() => import('./jobs/components/JobList'), { fallback: <RouteLoader /> });
const MessageStatus = loadable(() => import('./settings/messagestatus/components/MessageStatus'), { fallback: <RouteLoader /> });
const MySignedForms = loadable(() => import('./mysignedforms/MySignedForms'), { fallback: <RouteLoader /> });
const Notifications = loadable(() => import('./profile/Notifications'), { fallback: <RouteLoader /> });
const PreviewFeatures = loadable(() => import('./settings/previewfeatures/PreviewFeatures'), { fallback: <RouteLoader /> });
const Profile = loadable(() => import('./profile/Profile'), { fallback: <RouteLoader /> });
const PublicForm = loadable(() => import('./formeditor/components/PublicForm'), { fallback: <RouteLoader /> });
const PublicFormPdf = loadable(() => import('./formeditor/components/PublicFormPdf'), { fallback: <RouteLoader /> });
const PublicShareList = loadable(() => import('./publicshare/PublicShareList'), { fallback: <RouteLoader /> });
const Publishing = loadable(() => import('./settings/publishing/Publishing'), { fallback: <RouteLoader /> });
const ReportList = loadable(() => import('./reports/components/ReportList'), { fallback: <RouteLoader /> });
const SavedPublicForm = loadable(() => import('./formeditor/components/SavedPublicForm'), { fallback: <RouteLoader /> });
const SavingsEstimate = loadable(() => import('./welcome/home/SavingsEstimate'), { fallback: <RouteLoader /> });
const SelectView = loadable(() => import('./templateeditor/components/SelectView'), { fallback: <RouteLoader /> });
const SettingsPage = loadable(() => import('./settings/components/SettingsPage'), { fallback: <RouteLoader /> });
const StarterTemplates = loadable(() => import('./templates/components/StarterTemplates'), { fallback: <RouteLoader /> });
const Subscription = loadable(() => import('./subscription/components/Subscription'), { fallback: <RouteLoader /> });
const Subscription_Old = loadable(() => import('./subscription/components/Subscription_Old'), { fallback: <RouteLoader /> });
const TemplateEditorJson = loadable(() => import('./templateeditor/components/TemplateEditorJson'), { fallback: <RouteLoader /> });
const TemplateEditor = loadable(() => import('./templateeditor/components/TemplateEditor'), { fallback: <RouteLoader /> });
const TemplateEvents = loadable(() => import('./templateevents/TemplateEvents'), { fallback: <RouteLoader /> });
const TemplateList = loadable(() => import('./templates/components/TemplateList'), { fallback: <RouteLoader /> });
const UsageMetric = loadable(() => import('./settings/usagemetrics/components/UsageMetric'), { fallback: <RouteLoader /> });
const UsersGroupsHub = loadable(() => import('./usersgroups/UsersGroupsHub'), { fallback: <RouteLoader /> });
const UsersGroupsRouter = loadable(() => import('./usersgroups/UsersGroupsRouter'), { fallback: <RouteLoader /> });
const Viewer = loadable(() => import('./reports/components/Viewer'), { fallback: <RouteLoader /> });
const WebhookCreate = loadable(() => import('./webhooks/components/WebhookCreate'), { fallback: <RouteLoader /> });
const WebhookEdit = loadable(() => import('./webhooks/components/WebhookEdit'), { fallback: <RouteLoader /> });
const WebhookList = loadable(() => import('./webhooks/components/WebhookList'), { fallback: <RouteLoader /> });
const WebhookRequestDetails = loadable(() => import('./webhooks/components/WebhookRequestDetails'), { fallback: <RouteLoader /> });
const WebhookRequestList = loadable(() => import('./webhooks/components/WebhookRequestList'), { fallback: <RouteLoader /> });
const WorkflowDashboard = loadable(() => import('./workflowdashboard/components/WorkflowDashboard'), { fallback: <RouteLoader /> });
const WorkflowEditor = loadable(() => import('./workfloweditor/components/WorkflowEditor'), { fallback: <RouteLoader /> });
const WorkflowEditorJson = loadable(() => import('./workfloweditor/components/WorkflowEditorJson'), { fallback: <RouteLoader /> });
const WorkflowHub = loadable(() => import('./workflowhub/WorkflowHub'), { fallback: <RouteLoader /> });
const WorkflowList = loadable(() => import('./workflows/components/Workflows'), { fallback: <RouteLoader /> });
const WorkflowSelectWrapper = loadable(() => import('./workflowselect/WorkflowSelectWrapper'), { fallback: <RouteLoader /> });
const WorkflowUpgradeScreen = loadable(() => import('./workflows/components/WorkflowUpgradeScreen'), { fallback: <RouteLoader /> });

// todo - subscription_old requires an hoc to provide "history" (which is no longer supported by the new router).
//        replace with an hoc that adds the navigate function instead
const FeaturedSubscription = (props) => (canIUse(Features.SubscriptionUIRefresh) ? <Subscription /> : <Subscription_Old {...props} />);

export const routes = createRoutesFromChildren(
  // NOTE - if this gets too large, consider moving child routes into the parent components
  //        at each parent route. for now, keep them here for clarity.
  <Route element={<LoggingAndRedirectLayout />}>
    {/* these public share/forms routes do not require authentication  */}
    <Route element={<PublicShareLayout />}>
      <Route path="/shared/:sharedAccessToken" element={<PublicForm />} />
      <Route path="/s/:sharedAccessToken" element={<PublicForm />} />
      <Route path="/s/f/:sharedAccessToken" element={<PublicFormPdf />} />
      <Route path="/s/saved/:saveId" element={<SavedPublicForm />} />
    </Route>
    {/* this route does not require authentication */}
    <Route path="/accountdeleted" element={<AccountDeleted />} />

    {/* these routes require auth, but doesn't render the the main app header */}
    <Route element={<HeaderlessAuthLayout />}>
      <Route path="/app/authreturn" element={<RouteLoader />} />
      <Route path="/loading" element={<Loader isLoading />} />
      <Route path="/survey" element={<OnboardingSurveyRouter />} />
      <Route path="/survey/complete" element={<OnboardingFinal />} />
      <Route path="/expiredtrial" element={<ExpiredTrial />} />
      <Route path="/iuprofile" element={<IuProfile />} />
    </Route>

    {/* all other routes that are authenticated and require the main app header render within the MainLayout/SplashPageV2/RootV2/Main */}
    <Route element={<MainLayout />}>
      {/* <WrappedLayout /> is the <Wrapper /> component from the old v5 router that wraps 98% of our child route components */}
      <Route element={<WrappedLayout />}>
        <Route path="/" element={<IndexRedirect />} />

        {/* DATASOURCE ROUTES */}
        <Route path="/datasource/*">
          <Route element={<FeatureFlaggedLayout feature={AppFeatures.ManageDataSources} />}>
            <Route index element={<DatasourceList />} />
            <Route path="edit" element={<DatasourceEditor />} />
          </Route>
        </Route>

        {/* DROPDOWN COLLECTION ROUTES */}
        <Route path="/dropdowncollections/*">
          <Route index element={<ItemCollectionList />} />
          <Route path="new" element={<ItemCollectionEdit new />} />
          <Route path=":id" element={<ItemCollectionEdit />} />
        </Route>

        {/* FORM ROUTES */}
        <Route path="/editor" element={<EditorRedirect />} />
        <Route path="/form" element={<Navigate to="/forms" replace />} />
        <Route path="/forms/*">
          <Route
            index
            element={
              <FeatureFlagged feature={AppFeatures.FormList} upgradeRequired>
                <Forms />
              </FeatureFlagged>
            }
          />
          <Route element={<FormEditorWrapper />}>
            <Route
              // todo - this route renders nothing in dev/beta/prod...is it needed?
              //        should it open the select template modal?
              path="new"
              element={<FormEditor />}
            />
            <Route path=":id" element={<FormEditor />} />
          </Route>
        </Route>

        {/* HOME ROUTES */}
        <Route path="/home/*">
          <Route index element={<HomeRedirect />} />
          <Route path="savings" element={<SavingsEstimate />} />
        </Route>

        {/* PUBLIC FORMS ROUTE */}
        <Route
          path="/publicforms"
          element={
            <FeatureFlagged feature={AppFeatures.PublicShare}>
              <PublicShareList />
            </FeatureFlagged>
          }
        />

        {/* REPORT ROUTES */}
        <Route path="/reports/*">
          <Route
            element={
              <FeatureFlaggedLayout feature={AppFeatures.Reports}>
                {/* If the user doesn't have access to reports but is an admin, shows the upsell page*/}
                <ReportUpsell />
              </FeatureFlaggedLayout>
            }
          >
            <Route index element={<ReportList />} />
            <Route path="designer" element={<DesignerPage />} />
            <Route path="viewer" element={<Viewer />} />
          </Route>
        </Route>

        {/* SETTINGS ROUTES */}
        {/* NOTE: the settings pages are only parented by the <SettingsPage /> component, not by a /settings route */}
        <Route element={<SettingsPage />}>
          <Route path="/profile" element={<Profile />} />
          <Route
            path="/apicredentials"
            element={
              <FeatureFlagged feature={AppFeatures.ApiCredentialsUI}>
                <ApiCredentials />
              </FeatureFlagged>
            }
          />
          <Route
            path="/applications"
            element={
              <WorkflowRecipeProvider>
                <FeatureFlagged feature={AppFeatures.ConnectedApps}>
                  <AppList />
                </FeatureFlagged>
              </WorkflowRecipeProvider>
            }
          />
          <Route
            path="/autoemail"
            element={
              <FeatureFlagged feature={AppFeatures.ManageAutoEmail}>
                <AutoEmail />
              </FeatureFlagged>
            }
          />
          <Route
            path="/messagestatus"
            element={
              <FeatureFlagged feature={AppFeatures.MessageStatus}>
                <MessageStatus />
              </FeatureFlagged>
            }
          />
          <Route
            path="/mysignedforms"
            element={
              <FeatureFlagged feature={AppFeatures.eSignature} upgradeRequired>
                <MySignedForms />
              </FeatureFlagged>
            }
          />
          <Route
            path="/notifications"
            element={
              <FeatureFlagged feature={AppFeatures.ProfileNotifications}>
                <Notifications />
              </FeatureFlagged>
            }
          />
          <Route
            path="/previewfeatures"
            element={
              <FeatureFlagged feature={AppFeatures.PreviewFeatures}>
                <PreviewFeatures />
              </FeatureFlagged>
            }
          />
          <Route element={<FeatureFlaggedLayout feature={AppFeatures.ManageSubscription} />}>
            <Route
              path="/billing"
              element={
                <BillingProvider>
                  <BillingPage />
                </BillingProvider>
              }
            />
            <Route path="/subscription" element={<FeaturedSubscription />} />
          </Route>
          <Route element={<FeatureFlaggedLayout feature={AppFeatures.ManageSettings} />}>
            <Route path="/settings" element={<AccountSettings />} />
            {/* 
                todo - /publishing page is controlled by AppFeatures.ManageSettings flag, but the nav menu item is
                       controlled by Features.TemplatePublising...
             */}
            <Route path="/publishing" element={<Publishing />} />
            <Route path="/usage" element={<UsageMetric />} />
          </Route>

          {/* Users and Groups routes */}
          <Route path="/users/*" element={<UsersRedirect />} />
          <Route path="/manage" element={<Navigate to="/manage/users" />} />
          <Route path="/manage/*" element={<FeatureFlaggedLayout feature={AppFeatures.ManageUsers} />}>
            <Route element={<UsersGroupsHub />}>
              <Route path="users" element={<UsersGroupsRouter />} />
              <Route path="groups" element={<UsersGroupsRouter />} />
            </Route>
            <Route path="users/create" element={<AddUser />} />
            <Route
              path="users/create/bulk"
              element={
                <FeatureFlagged feature={AppFeatures.ManageUsersAdmin}>
                  <BulkAddUser />
                </FeatureFlagged>
              }
            />
            <Route path="users/:userId" element={<EditUser />} />
            <Route
              path="groups/create"
              element={
                <FeatureFlagged feature={AppFeatures.ManageUsersAdmin}>
                  <AddGroup />
                </FeatureFlagged>
              }
            />
            <Route
              path="groups/:groupId"
              element={
                <FeatureFlagged feature={AppFeatures.ManageUsersAdmin}>
                  <EditGroup />
                </FeatureFlagged>
              }
            />
          </Route>
          {/* this is a special, one-off, feature of workflows */}
          <Route
            path="/workflows/:workflowId/json"
            element={
              <FeatureFlagged feature={AppFeatures.WorkflowJson}>
                <WorkflowEditorJson />
              </FeatureFlagged>
            }
          />

          <Route path="/webhooks/*" element={<FeatureFlaggedLayout feature={AppFeatures.WebhooksUI} />}>
            <Route index element={<WebhookList />} />
            <Route path=":id" element={<WebhookEdit />} />
            <Route path="create" element={<WebhookCreate />} />
            <Route path="requests" element={<WebhookRequestList />} />
            <Route path="requests/:id" element={<WebhookRequestDetails />} />
          </Route>
        </Route>

        {/* TEMPLATE ROUTES */}
        <Route path="/createtemplate" element={<CreateTemplateModalPage />} />
        <Route path="/template/*" element={<TemplateRedirect />} />
        <Route path="/templates/*">
          <Route path="starter" element={<StarterTemplates />} />
          <Route element={<FeatureFlaggedLayout feature={AppFeatures.TemplateList} />}>
            <Route index element={<TemplateList />} />
            <Route path="new/selectview" element={<SelectView />} />
            <Route path=":id" element={<TemplateEditor />} />
            <Route path="new" element={<TemplateEditor new />} />
            <Route path="new/:view" element={<TemplateEditor new />} />
          </Route>
          <Route
            path=":id/json"
            element={
              <FeatureFlagged feature={AppFeatures.TemplateEditorJson}>
                <TemplateEditorJson />
              </FeatureFlagged>
            }
          />
          <Route
            path=":templateId/events"
            element={
              <FeatureFlagged feature={Features.TemplateEvents}>
                <TemplateEvents />
              </FeatureFlagged>
            }
          />
        </Route>

        {/* WORKFLOW ROUTES */}
        <Route path="/workflow/*" element={<WorkflowRedirect />} />
        <Route
          path="/workflows/*"
          element={
            <WorkflowRecipeProvider>
              <FeatureFlaggedLayout feature={AppFeatures.Workflow}>
                <WorkflowUpgradeScreen />
              </FeatureFlaggedLayout>
            </WorkflowRecipeProvider>
          }
        >
          <Route element={<WorkflowHub />}>
            <Route index element={<WorkflowList />} />
            <Route path="jobs" element={<JobList />} />
            <Route path="dashboard" element={<WorkflowDashboard />} />
          </Route>
          <Route path="select" element={<WorkflowSelectWrapper />} />
          <Route path="create" element={<WorkflowEditor />} />
          <Route path="jobs/:jobId" element={<JobDetails />} />
          <Route path=":workflowId" element={<WorkflowEditor />} />
        </Route>
        {/* this is a special, one-off, feature of workflows */}
        <Route
          path="/workflows/:workflowId/json"
          element={
            <FeatureFlagged feature={AppFeatures.WorkflowJson}>
              <WorkflowEditorJson />
            </FeatureFlagged>
          }
        />

        {/* OTHERS AND NON-PARENTED ROUTES */}
        <Route
          path="/applications/:appId"
          element={
            <FeatureFlagged feature={AppFeatures.ConnectedApps}>
              <AppDetails />
            </FeatureFlagged>
          }
        />
        <Route path="/error/*">
          <Route index element={<Error />} />
          <Route path="404" element={<Error errorCode={404} />} />
        </Route>
        <Route path="*" element={<Error errorCode={404} />} />
      </Route>
    </Route>
  </Route>
);
